import type { LandingPage } from "./types";

/**
 * How each published hero is laid out, as measured off the live site.
 *
 * These are not editorial choices and they are deliberately not in the CMS. An
 * editor writes a hero's words, picks its picture and its buttons; nobody
 * chooses "pad the box to 32 rather than 48" or "set the title 32 off the proof
 * row rather than 16". Those are facts about how the published page was built,
 * they change only when the design does, and a wrong value is a layout defect
 * rather than a content mistake. Facts like that belong in code, under review,
 * where a change shows up in a diff.
 *
 * Keyed by path, with a default per page family, because that is how the
 * published site varies: the eleven use-case heroes are identical to each
 * other, the four comparison heroes are identical to each other, and the
 * product pages differ one from the next.
 */
export type HeroLayout = {
  /** `hero_integrate_wrap`: the one published hero set centre over its logo marquee. */
  align?: "center";
  /** Draws the art at its own size; every other hero draws it in a box of one fixed height. */
  naturalVisual?: true;
  /** Sets the title further off the proof row: 32 at the wide step rather than 16. */
  looseProof?: true;
  /**
   * Which wash the box carries. Absent is the blue-grey most pages draw, which
   * the campaign pages' `is-resi` box keeps. The last three are the partner
   * campaign boxes: `is-blackdown`, `is-proxyway` (which `is-promo` repeats)
   * and `is-vpncenter`.
   */
  wash?: "blush" | "surface" | "mint" | "lilac" | "aqua" | "blossom" | "amber";
  /**
   * `.main_header_box.is-proxyway`: the partner campaign hero. The box pads 64
   * a side, the copy takes half of it at 16 apart, and the partner's badge
   * fills the other half as a square. The proof row heads the column rather
   * than the title group.
   */
  partner?: true;
  /**
   * `.main_header_box.is-promo`: the partner box's padding around the
   * standard column and art, the proof row heading the column, and the title
   * set `.h1-new.is-hero`: 20 characters wide, a weight heavier.
   */
  promo?: true;
  /**
   * `.proxyway_wrap` without `is-ppi`: on a phone the review badge keeps the
   * row's width and shows its tint at rest.
   */
  reviewFill?: true;
  /**
   * How wide the controls draw. `hug` fits each to its label and closes a pair
   * to 12, `fixed` sets the first to 200, `wide` sets it to 220 and stops it
   * shrinking. Absent fills the column, or takes 283 for a lone control.
   */
  ctaFit?: "hug" | "fixed" | "wide";
  /**
   * `.buttons-header_wrap` and `.buttons-left_wrap`: the pair keeps its row
   * down to the tiny step, where the published wrap stacks it. Absent, a pair
   * stacks at the card step.
   */
  pairRow?: true;
  /**
   * `.buttons-left_wrap`: the pair's row sized to its controls rather than
   * the column, so the second control's 100% is of that row.
   */
  ctaRowFits?: true;
  /**
   * The published column holds an empty `main_header_button_holder` before
   * its controls, which takes one more of the column's gaps.
   */
  ghostControl?: true;
  /** The hero that is a centred section heading rather than a hero box. */
  sectionHeading?: true;
  /** `body_new.is-medium`: the lead paragraph set in the heavier weight. */
  leadEmphasis?: true;
  /** Sets the checks on a panel rather than loose under the copy. */
  checksPanel?: true;
  /** Sets the copy to the narrower of the two published measures. */
  narrowColumn?: true;
  /** The hero sits 24px lower, on the section symbol inside the banner spacer. */
  bannerSpacer?: true;
  /** `.aff-ref-tab_wrap`'s margin: the hero opens 48 under the earn pages' switcher, 16 below the card step. */
  afterTabs?: true;
  /** Pads the hero box to 32 rather than 48. */
  compactBox?: true;
  /** The Trustpilot rating strip, between the breadcrumb and the title. */
  trustpilot?: true;
  /** `.b-crumble_wrap.is-use-case`: the use-case pages' own breadcrumb wrap. */
  useCaseTrail?: true;
  /** `.pricing_button_secondary`: the second control in the page's ink on a 1.5px accent rule. */
  pricingSecondary?: true;
  /** `.button_new.is_hero`: the first control 14rem wide, the row's width from 767 down. */
  heroControl?: true;
  /**
   * `.header-hp-ttx-bt_wrap`: the line under the control drops to 13 on a
   * phone, where every other hero's `.main_header_text_btn_holder` keeps 14.
   */
  chipsShrink?: true;
  /**
   * `.h1-new.is-centered` alone at the top of the ethics page's flowing
   * column: no box and no controls, on the column's 1100 measure, 112 under
   * the header and 80 below the wide step, with the first block 32 under it.
   */
  bare?: true;
};

/**
 * What a family draws unless a page says otherwise. Measured: all eleven
 * use-case heroes agree, all four comparison heroes agree, and all four
 * competitor heroes agree.
 */
const BY_GROUP: Partial<Record<LandingPage["group"], HeroLayout>> = {
  useCase: {
    naturalVisual: true,
    narrowColumn: true,
    trustpilot: true,
    useCaseTrail: true,
  },
  alternative: { align: "center", wash: "surface" },
  competitor: { checksPanel: true, trustpilot: true, pricingSecondary: true },
};

/** `is-blackdown`, the partner box five campaign pages share. */
const PARTNER_AQUA: HeroLayout = { partner: true, wash: "aqua" };
/** `is-resi`: the standard box on the narrow column, the review row over the title. */
const CAMPAIGN_RESI: HeroLayout = { narrowColumn: true, reviewFill: true };
/** `is-promo`. */
const CAMPAIGN_PROMO: HeroLayout = {
  promo: true,
  wash: "blossom",
  reviewFill: true,
};

/**
 * Where a page differs from its family, or belongs to none. Read off the
 * published pages: eighteen of the thirty-seven heroes state something their
 * family does not, and the two empty entries are pages whose family does state
 * one and which draw none of it.
 */
const BY_PATH: Record<string, HeroLayout> = {
  "/l/blackdown": PARTNER_AQUA,
  "/l/cvamp": PARTNER_AQUA,
  "/l/cybernews": PARTNER_AQUA,
  "/l/designrush": PARTNER_AQUA,
  "/l/dginnovations": PARTNER_AQUA,
  "/l/jdepoix": CAMPAIGN_RESI,
  "/l/privateproxyreviews": CAMPAIGN_RESI,
  "/l/promo": CAMPAIGN_PROMO,
  "/l/promo15": CAMPAIGN_PROMO,
  "/l/proxies": CAMPAIGN_RESI,
  "/l/proxyway": { partner: true, wash: "blossom", reviewFill: true },
  "/l/vpncentral": { partner: true, wash: "amber" },
  // The product variants: the standard box, as the product pages draw it.
  "/l/dedicated-isp-proxies": { looseProof: true, trustpilot: true },
  "/l/dedicated-proxy": { looseProof: true, trustpilot: true },
  "/l/residential-proxy": {
    narrowColumn: true,
    trustpilot: true,
    checksPanel: true,
    ctaFit: "fixed",
    pairRow: true,
    ctaRowFits: true,
    ghostControl: true,
  },
  "/l/rotating-residential-proxy": {
    narrowColumn: true,
    trustpilot: true,
    checksPanel: true,
  },
  "/l/static-residential-proxies": {
    trustpilot: true,
    ctaFit: "fixed",
    pairRow: true,
  },
  "/affiliate-program": { trustpilot: true, afterTabs: true },
  "/chrome-proxy-extension": {
    checksPanel: true,
    compactBox: true,
    narrowColumn: true,
    trustpilot: true,
  },
  "/dedicated-datacenter-proxy": {
    bannerSpacer: true,
    ctaFit: "hug",
    wash: "mint",
    trustpilot: true,
  },
  "/dedicated-proxy": { ctaFit: "fixed", narrowColumn: true, trustpilot: true },
  "/dedicated-static-residential-proxy": {
    ctaFit: "hug",
    wash: "lilac",
    trustpilot: true,
  },
  "/frequently-asked-questions": {},
  "/integrations": { align: "center", heroControl: true },
  "/private-datacenter-proxy": {
    ctaFit: "hug",
    wash: "mint",
    trustpilot: true,
  },
  "/private-proxy": { ctaFit: "fixed", narrowColumn: true, trustpilot: true },
  "/private-static-residential-proxy": {
    ctaFit: "hug",
    wash: "lilac",
    trustpilot: true,
  },
  "/product-comparison": { sectionHeading: true },
  "/proxy-server": { bannerSpacer: true, checksPanel: true, ctaFit: "wide" },
  "/referral-program": { trustpilot: true, afterTabs: true },
  "/residential-proxy": { checksPanel: true, pairRow: true },
  "/rotating-proxy": {
    narrowColumn: true,
    trustpilot: true,
    chipsShrink: true,
  },
  "/static-residential-proxy": {
    bannerSpacer: true,
    ctaFit: "fixed",
    pairRow: true,
  },
  "/webshares-ethically-sourced-residential-proxies": { bare: true },
  "/youtube-proxy": {
    ctaFit: "hug",
    wash: "blush",
    leadEmphasis: true,
    looseProof: true,
    narrowColumn: true,
    trustpilot: true,
  },
};

/** The layout the page's hero draws, its family's unless the page states its own. */
export function heroLayout(page: {
  path: string;
  group: LandingPage["group"];
}): HeroLayout {
  return BY_PATH[page.path] ?? BY_GROUP[page.group] ?? {};
}
